import { Button, ButtonGroup, Flex, Text } from '@chakra-ui/react';
import React from 'react';

interface PaginationProps {
	page: number;
	totalItems: number;
	pageSize: number;
	onPageChange: (page: number) => void;
}

function getVisiblePages(currentPage: number, totalPages: number): number[] {
	if (totalPages <= 7) {
		return Array.from({ length: totalPages }, (_, index) => index + 1);
	}

	if (currentPage <= 4) {
		return [1, 2, 3, 4, 5, -1, totalPages];
	}

	if (currentPage >= totalPages - 3) {
		return [1, -1, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
	}

	return [1, -1, currentPage - 1, currentPage, currentPage + 1, -1, totalPages];
}

export function Pagination({
	page,
	totalItems,
	pageSize,
	onPageChange,
}: PaginationProps): JSX.Element | null {
	const safePage = Math.max(1, page);
	const safePageSize = Math.max(1, pageSize);
	const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));

	if (totalItems <= 0 || totalPages <= 1) {
		return null;
	}

	const startItem = (safePage - 1) * safePageSize + 1;
	const endItem = Math.min(safePage * safePageSize, totalItems);
	const pages = getVisiblePages(safePage, totalPages);
	const activeButtonStyles = {
		bg: '#F27039',
		borderWidth: '1px',
		borderColor: 'gray.200',
		color: 'white',
		_hover: {
			bg: '#F27039',
			borderColor: 'gray.200',
		},
	};
	const inactiveButtonStyles = {
		bg: 'white',
		borderWidth: '1px',
		borderColor: 'gray.200',
		color: 'gray.800',
		_hover: {
			bg: '#F27039',
			borderColor: 'gray.200',
			color: 'white',
		},
	};

	return (
		<Flex
			mt={4}
			alignItems="center"
			justifyContent="space-between"
			flexWrap="wrap"
			gap={3}>
			<Text fontSize="sm" color="gray.600">
				Visar {startItem}-{endItem} av {totalItems}
			</Text>

			<ButtonGroup isAttached size="sm">
				<Button
					onClick={() => onPageChange(safePage - 1)}
					isDisabled={safePage <= 1}
					{...inactiveButtonStyles}>
					Föregående
				</Button>

				{pages.map((pageNumber, index) => {
					if (pageNumber === -1) {
						return (
							<Button key={`ellipsis-${index}`} isDisabled bg="white" borderWidth="1px" borderColor="gray.200" color="gray.700">
								...
							</Button>
						);
					}

					const isCurrentPage = pageNumber === safePage;
					return (
						<Button
							key={pageNumber}
							onClick={() => onPageChange(pageNumber)}
							{...(isCurrentPage ? activeButtonStyles : inactiveButtonStyles)}>
							{pageNumber}
						</Button>
					);
				})}

				<Button
					onClick={() => onPageChange(safePage + 1)}
					isDisabled={safePage >= totalPages}
					{...inactiveButtonStyles}>
					Nästa
				</Button>
			</ButtonGroup>
		</Flex>
	);
}
